import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import {
  AccountLinkCellComponent,
  ActionsCellComponent,
  AlarmCdoSeverityCellComponent,
  AlarmStateCellComponent,
  AlarmStateChangedCellComponent,
  CloudEnvironment,
  CloudEnvironmentHealthCheck,
  DataBindingCellComponent,
  DevOpsAlarm,
  ErmDevOpsApiClientService
} from 'erm-sdk';
import { HealthCheckViewModel } from './health-check.viewmodel';
import * as _ from 'lodash';

@Component({
  standalone: false,
  templateUrl: './environment-details.component.html',
  styleUrls: ['./environment-details.component.scss']
})
export class EnvironmentDetailsComponent implements OnInit {

  isLoading = false;
  alarmGridOptions: GridOptions;
  alarmGridApi?: GridApi;
  healthCheckGridOptions: GridOptions;
  healthCheckGridApi?: GridApi;
  environmentId: string;
  environment?: CloudEnvironment;

  @ViewChild('agGrid') agGrid!: AgGridAngular;

  healthCheckVms?: HealthCheckViewModel[];

  constructor(private ermDevOpsApiClient: ErmDevOpsApiClientService, route: ActivatedRoute) {
    this.environmentId = route.snapshot.params['environmentId'];

    this.alarmGridOptions = {
      columnDefs: this.createAlarmsGridColumnDefs(),
      enableCellTextSelection: true,
      rowSelection: { mode: 'singleRow' },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.alarmGridApi = params.api;
      }
    };

    this.healthCheckGridOptions = {
      columnDefs: this.createHealtchCheckGridColumnDefs(),
      enableCellTextSelection: true,
      rowSelection: { mode: 'singleRow' },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.healthCheckGridApi = params.api;
      }
    };
  }

  async ngOnInit(): Promise<void> {
    try {

      this.isLoading = true;

      // Get environment
      const getEnvironmentResponse = await this.ermDevOpsApiClient.getCloudEnvironment(this.environmentId);
      this.environment = getEnvironmentResponse.body?.cloudEnvironment;

      // Get environment alarms
      const getEnvironmentAlarmsResponse = await this.ermDevOpsApiClient.getCloudEnvironmentAlarms(this.environmentId);
      const alarms = getEnvironmentAlarmsResponse.body?.alarms || new Array<DevOpsAlarm>();

      // Get environment health checks
      const getEnvironmentHealthChecksResponse = await this.ermDevOpsApiClient.getCloudEnvironmentHealthChecks(this.environmentId);
      const healthChecks = getEnvironmentHealthChecksResponse.body?.healthChecks || new Array<CloudEnvironmentHealthCheck>();
      this.healthCheckVms = _.map(healthChecks, x => new HealthCheckViewModel(this.ermDevOpsApiClient, x));

      // Initialize grid
      this.alarmGridApi?.setGridOption('rowData', alarms);
      this.healthCheckGridApi?.setGridOption('rowData', this.healthCheckVms);

    } finally {
      this.isLoading = false;
    }
  }

  private createAlarmsGridColumnDefs(): any[] {
    return [
      {
        headerName: 'State',
        field: 'alarmState',
        cellRendererFramework: AlarmStateCellComponent,
        width: 120,
        sort: 'asc'
      },
      {
        headerName: 'State Changed',
        field: 'alarmStateUpdatedUtc',
        cellRendererFramework: AlarmStateChangedCellComponent,
        width: 150
      },
      { headerName: 'Name', field: 'alarmName', width: 950, sort: 'asc' },
      {
        headerName: 'Account',
        field: 'accountId',
        cellRendererFramework: AccountLinkCellComponent,
        width: 110
      },
      { headerName: 'Service Level', field: 'serviceLevel', width: 140 },
      {
        headerName: 'CDO Severity',
        field: 'cdoSeverity',
        cellRendererFramework: AlarmCdoSeverityCellComponent,
        width: 140
      },
      { headerName: 'Resource', field: 'resourceName', width: 250 },
      { headerName: 'Metric', field: 'metricName' },
      { headerName: 'Description', field: 'description', width: 350 }
    ];
  }

  private createHealtchCheckGridColumnDefs(): any[] {
    return [
      {
        headerName: 'Actions',
        cellRendererFramework: ActionsCellComponent,
        cellRendererParams: {
          actions: [{
            titleField: 'runTitle',
            statusField: 'runStatus',
            execute: async (healthCheckVm: HealthCheckViewModel) => {
              await healthCheckVm.run();
            },
            canExecute: (healthCheckVm: HealthCheckViewModel) => {
              return !healthCheckVm.isRunning;
            }
          }],
        }
      },
      { headerName: 'Environment Id', field: 'environmentId', cellRendererFramework: DataBindingCellComponent },
      { headerName: 'Service Name', field: 'serviceName', cellRendererFramework: DataBindingCellComponent },
      { headerName: 'Result', field: 'result', cellRendererFramework: DataBindingCellComponent },
      { headerName: 'Result Time (UTC)', field: 'resultTimeUtc', cellRendererFramework: DataBindingCellComponent },
      { headerName: 'Duration (ms)', field: 'elapsedMilliseconds', cellRendererFramework: DataBindingCellComponent }
    ];
  }

  async runAllHealthChecks(): Promise<void> {
    _.each(this.healthCheckVms, x => x.run());
  }
}
